import { useSeoMeta } from '@unhead/react';
import { Feed } from '@/components/jp/Feed';
import { TRexSilhouette } from '@/components/jp/DinoArt';

const Index = () => {
  useSeoMeta({
    title: 'Jurassic Feed · Visitor Monitoring System',
    description:
      'A Nostr feed client themed like the Jurassic Park visitor center control room. See field transmissions from a curated network of researchers.',
  });

  return (
    <div className="min-h-screen text-foreground relative overflow-x-hidden">
      {/* Decorative background — oversized faded TRex */}
      <TRexSilhouette
        className="fixed pointer-events-none select-none text-jp-amber/[0.035] w-[900px] h-[900px] -right-40 top-10 z-0"
      />

      <div className="relative z-10">
        <SiteHeader />

        <main className="max-w-2xl mx-auto px-4 pb-24 pt-6">
          <Feed />
        </main>

        <SiteFooter />
      </div>
    </div>
  );
};

/* =======================  HEADER  ======================= */

function SiteHeader() {
  return (
    <header className="relative">
      {/* Top hazard stripe */}
      <div className="h-3 jp-hazard-stripes" />

      {/* Marquee ticker */}
      <div className="bg-[hsl(120_40%_4%)] border-b border-jp-amber/30 overflow-hidden">
        <div className="jp-ticker py-1 font-mono text-[0.7rem] text-jp-amber/80 tracking-widest">
          {Array.from({ length: 2 }).map((_, i) => (
            <TickerContent key={i} />
          ))}
        </div>
      </div>

      {/* Main nav */}
      <div className="bg-gradient-to-b from-[hsl(120_40%_6%)] to-[hsl(120_30%_4%)] border-b-2 border-jp-amber/50">
        <div className="max-w-5xl mx-auto px-4 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <LogoBadge />
            <div className="hidden sm:block min-w-0">
              <h1 className="font-display text-jp-amber-bright jp-electric text-2xl md:text-3xl leading-none tracking-wide">
                JURASSIC FEED
              </h1>
              <p className="font-mono text-[0.65rem] text-jp-amber/60 tracking-[0.25em] mt-1">
                VISITOR MONITORING SYSTEM · v1.0
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 font-mono text-[0.7rem] text-jp-amber/80">
              <span className="jp-led" />
              <span>MONITOR LIVE</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom caution hazard tiny strip */}
      <div className="h-1.5 jp-hazard-stripes opacity-80" />
    </header>
  );
}

function TickerContent() {
  const items = [
    'PERIMETER FENCE: ONLINE',
    'RAPTOR PADDOCK: CONTAINED',
    'T-REX PADDOCK: MONITORING',
    'VISITOR CENTER: OPERATIONAL',
    'SYS/COMMS: NOMINAL',
    'WELCOME… TO JURASSIC FEED',
    'OBJECTS IN MIRROR ARE CLOSER THAN THEY APPEAR',
    'HOLD ONTO YOUR BUTTS',
  ];
  return (
    <>
      {items.map((text, i) => (
        <span key={i} className="flex items-center gap-2">
          <span className="text-jp-red">◆</span>
          <span>{text}</span>
        </span>
      ))}
    </>
  );
}

function LogoBadge() {
  return (
    <div className="relative shrink-0">
      <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-jp-red border-4 border-jp-bone flex items-center justify-center shadow-[0_0_20px_hsl(352_75%_50%_/_0.5)]">
        <TRexSilhouette className="w-10 h-10 sm:w-12 sm:h-12 text-jp-bone" />
      </div>
      <div
        className="absolute -inset-1 rounded-full border border-jp-amber/50 pointer-events-none"
        aria-hidden
      />
    </div>
  );
}

/* =======================  FOOTER  ======================= */

function SiteFooter() {
  return (
    <footer className="relative border-t-2 border-jp-amber/40 mt-12">
      <div className="h-1 jp-hazard-stripes opacity-70" />
      <div className="max-w-5xl mx-auto px-4 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 font-mono text-[0.65rem] tracking-wider text-jp-amber/60">
        <div className="flex items-center gap-2">
          <span className="jp-led" />
          <span>INGEN SYSTEMS · SITE B SIMULATION</span>
        </div>
        <div>
          Vibed with{' '}
          <a
            href="https://soapbox.pub/mkstack"
            target="_blank"
            rel="noopener noreferrer"
            className="text-jp-amber-bright hover:underline"
          >
            MKStack
          </a>
        </div>
      </div>
    </footer>
  );
}

export default Index;
